import React from 'react';
import { Avatar, AvatarProps, Badge, styled, useTheme } from '@mui/material';

const StyledAvatar = styled(Avatar)<{ size: number }>(({ size }) => ({
  textTransform: 'uppercase',
  height: `${size}rem`,
  width: `${size}rem`,
}));

const StyledBadge = styled(Badge)`
  .MuiBadge-badge {
    width: 4rem;
    height: 1.5rem;
    right: 50%;
    transform: translateX(50%) translateY(50%);
    color: #066626;
    background-color: #b0e1c0;
    font-weight: 700;
    padding: 0.5rem;
  }
`;

export const XXXS_AVATAR_SIZE = 2;
export const XXS_AVATAR_SIZE = 2.5;
export const XS_AVATAR_SIZE = 3;
export const SMALL_AVATAR_SIZE = 4;
export const MEDIUM_AVATAR_SIZE = 4.5;
export const LARGE_AVATAR_SIZE = 5;

type UserType = {
  firstName: string | null;
  lastName: string | null;
  smallAvatar?: string | null;
  mediumAvatar?: string | null;
};

export interface MoAvatarProps extends Omit<AvatarProps, 'src' | 'children'> {
  user: UserType;
  size?:
    | typeof XXXS_AVATAR_SIZE
    | typeof XXS_AVATAR_SIZE
    | typeof XS_AVATAR_SIZE
    | typeof SMALL_AVATAR_SIZE
    | typeof MEDIUM_AVATAR_SIZE
    | typeof LARGE_AVATAR_SIZE;
  label?: string;
}

export function MoAvatar({ user, size = MEDIUM_AVATAR_SIZE, label, sx, ...props }: MoAvatarProps) {
  const { firstName, lastName, mediumAvatar, smallAvatar } = user;

  const avatarSrc = mediumAvatar ?? smallAvatar;
  const theme = useTheme();

  const AvatarIcon = () => (
    <StyledAvatar
      sx={{
        backgroundColor: theme.palette.vineyardRose.lighter,
        color: theme.palette.vineyardRose.dark,
        ...sx,
      }}
      aria-hidden="true"
      size={size}
      {...(avatarSrc ? { src: avatarSrc } : {})}
      {...props}
    >
      {firstName?.[0]}
      {lastName?.[0]}
    </StyledAvatar>
  );

  return (
    <>
      {label ? (
        <StyledBadge
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          overlap="rectangular"
          badgeContent={label}
        >
          <AvatarIcon />
        </StyledBadge>
      ) : (
        <AvatarIcon />
      )}
    </>
  );
}
